/**
 * Colour and text for a manual order's status.
 *
 * Renders the same solid badge the Orders tab uses (`orderStatusBadge` +
 * `status*` in ManageOrders.css), so one status reads the same across the
 * terminal.
 *
 * Why a local table rather than importing `getOrderStatusClass`
 * ------------------------------------------------------------
 * Three reasons, in order of how much they would hurt:
 *
 *   1. It is not exported — it is declared inline inside `OrderListTable.js`,
 *      a 2000-line list component. Exporting it to reach in from a second tab
 *      is a worse coupling than the ten lines below.
 *   2. It calls `.toLowerCase()` unguarded, so it throws on a null status.
 *      A row that has not loaded yet would take the whole table down.
 *   3. `confirmed` and `label_generated` can never appear on a manual order,
 *      and the carrier-driven values can never be absent. A local table
 *      documents that closed set; a shared one would not.
 *
 * The values themselves mirror the backend `ManualOrderStatus`, which in turn
 * mirrors `OrderStatus` — so the classes map one-for-one and the two tabs
 * cannot drift apart visually.
 */

const CLASSES = {
  // Operator-driven: raised, then labelled.
  new: 'statusNewOrders',
  ready_to_ship: 'statusReadyToShip',

  // Carrier-driven, from here down.
  in_transit: 'statusInTransit',
  delivered: 'statusDelivered',
  exceptions: 'statusException',
  rto: 'statusRTO',
  held: 'statusHeld',
  lost: 'statusLost',
  damaged: 'statusDamaged',

  // Operator-driven and terminal.
  cancelled: 'statusCancelled',
};

/**
 * Badge class for one status value.
 *
 * An unknown or missing status stays grey rather than borrowing a colour that
 * would imply we understood it.
 */
export function manualOrderStatusClass(status) {
  if (!status) return 'statusReadyToShip';
  return CLASSES[status] || 'statusReadyToShip';
}

/** The badge's text. The server always sends `status_label`; the rest is paranoia. */
export function manualOrderStatusLabel(row) {
  if (!row) return '—';
  return row.status_label || row.status || '—';
}

/**
 * The carrier's own wording, for the line under the badge.
 *
 * Returns '' rather than a placeholder when nothing has been scanned yet: the
 * badge above already says where the order stands, so an empty second line is
 * honest. (The old pill needed a placeholder only because it *was* the status.)
 */
export function manualOrderProgressLine(row) {
  if (!row) return '';
  return row.progress_label || row.progress_raw_label || row.progress_raw_code || '';
}

export default manualOrderStatusClass;
